/**
 * Multi-word catalogue search — "battery 240" finding `BATTERY 240V`, without typing the name out.
 *
 * ---- Why the client has to do anything at all ----
 *
 * `items.get_items?q=` takes ONE string and matches it as a substring. That is fine for one word
 * and wrong for two: `q=battery 240` only matches names where those characters appear together in
 * that order, so `BATTERY 240V` hits but `BATTERY 12V 240AH` does not — and neither does anything
 * a purchaser types in a different order from the catalogue's own wording.
 *
 * Nobody remembers how the master phrases an item. They remember two or three facts about it.
 *
 * ---- The approach: ask broadly, narrow here ----
 *
 * Send the single most selective term to the server, then require EVERY term to appear in the
 * rows it returns. `battery 240` becomes `q=battery` plus a client-side filter for `240`.
 *
 * The trade-off is honest and bounded: the target must be inside the page the server returned for
 * the broad term. `pageWasTruncated` exists so the UI can say so rather than let a purchaser
 * conclude an item does not exist when it was simply past the limit. A backend that supported
 * token-AND search would remove this entirely — worth asking for, not worth waiting for.
 */

/** The words a purchaser typed, lower-cased, empties dropped. */
export function searchTerms(query: string): string[] {
  return query
    .trim()
    .toLowerCase()
    .split(/\s+/)
    .filter((t) => t !== '')
}

/**
 * The ONE term to send the server: the longest.
 *
 * Length is a decent proxy for selectivity — `battery` narrows the catalogue far more than `240`,
 * and a short numeric token like `2` would return a page of noise the real match might not be in.
 * Returns `''` for an empty query, which callers pass as "no filter".
 */
export function serverQueryFor(query: string): string {
  const terms = searchTerms(query)
  if (terms.length === 0) return ''
  return terms.reduce((longest, t) => (t.length > longest.length ? t : longest))
}

/** Whether a row satisfies EVERY term, in any order, across its name, code and group. */
export function matchesAllTerms(
  row: { code: string; name: string; group?: string },
  terms: readonly string[],
): boolean {
  if (terms.length === 0) return true
  const haystack = `${row.name} ${row.code} ${row.group ?? ''}`.toLowerCase()
  return terms.every((t) => haystack.includes(t))
}

/**
 * True when the server's page was full, so rows matching the other terms may exist beyond it.
 *
 * Never a silent cap: told "no matches", a purchaser reasonably concludes the item is not in the
 * catalogue and creates a duplicate.
 */
export function pageWasTruncated(returned: number, limit: number): boolean {
  return returned >= limit
}
